"use client"

import * as React from "react"

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { cn } from "@/lib/utils"

export type LeoInspectorSuggestionSeverity = "caution" | "review"

export function leoInspectorSuggestionDotClass(
  _severity: LeoInspectorSuggestionSeverity,
) {
  return "bg-destructive"
}

export function LeoInspectorSuggestionDotTrigger({
  severity = "caution",
  ariaLabel,
  focused = false,
  placement = "inline",
  className,
  onPointerEnter,
  onPointerLeave,
}: {
  severity?: LeoInspectorSuggestionSeverity
  ariaLabel: string
  focused?: boolean
  placement?: "inline" | "corner"
  className?: string
  onPointerEnter?: React.PointerEventHandler<HTMLButtonElement>
  onPointerLeave?: React.PointerEventHandler<HTMLButtonElement>
}) {
  return (
    <button
      type="button"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        placement === "corner"
          ? "absolute -right-1 -top-1 z-10 size-4"
          : "size-4 min-h-4 min-w-4 align-middle",
        focused && "ring-2 ring-brand ring-offset-2 ring-offset-background",
        className,
      )}
      aria-label={ariaLabel}
    >
      <span
        aria-hidden
        className={cn(
          "block size-2 rounded-full",
          leoInspectorSuggestionDotClass(severity),
        )}
      />
    </button>
  )
}

export function LeoInspectorSuggestionDot({
  severity = "caution",
  ariaLabel,
  focused = false,
  placement = "inline",
  children,
  contentClassName,
}: {
  severity?: LeoInspectorSuggestionSeverity
  ariaLabel: string
  focused?: boolean
  placement?: "inline" | "corner"
  children: React.ReactNode
  contentClassName?: string
}) {
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <LeoInspectorSuggestionDotTrigger
          severity={severity}
          ariaLabel={ariaLabel}
          focused={focused}
          placement={placement}
        />
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={placement === "corner" ? 12 : 8}
        collisionPadding={12}
        className={cn("w-[min(17rem,calc(100vw-2rem))] p-3", contentClassName)}
      >
        {children}
      </HoverCardContent>
    </HoverCard>
  )
}
